<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useAiChatStore } from '@/stores/ai-chat'
import { aiApi, type AttachmentMeta } from '@/api/ai'
import { Button } from '@kyoto2/components/ui/button'
import { Textarea } from '@kyoto2/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@kyoto2/components/ui/popover'
import { ArrowUp, AtSign, Check, ChevronDown, FileText, Loader2, Paperclip, Square, X } from 'lucide-vue-next'
import { toast } from 'vue-sonner'
import {
  type SlashCommand,
  classifySlashCommandInput,
  executesOnMenuSelect,
  filterCommands,
  slashMenuQuery,
  validateSlashCommandArgs,
} from '@/lib/ai-slash-commands'
import { mentionQuery, searchMentions, stripMentionQuery, type MentionItem } from '@/lib/ai-mentions'
import AiContextMeter from '@/components/ai/AiContextMeter.vue'

const props = defineProps<{
  placeholder?: string
  routeContext?: string
  currentYaml?: string
  connectorIds?: number[]
}>()

const emit = defineEmits<{
  send: []
}>()

const store = useAiChatStore()
const inputText = ref('')
const textareaRef = ref<InstanceType<typeof Textarea> | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const pendingAttachments = ref<Array<AttachmentMeta & { uploading?: boolean }>>([])
const dragOver = ref(false)

const menuOpen = ref(false)
const filtered = ref<SlashCommand[]>([])
const activeIndex = ref(0)
const menuRef = ref<HTMLElement | null>(null)
const SLASH_MENU_ID = 'ai-composer-slash-commands'
const MENTION_MENU_ID = 'ai-composer-mentions'

// @-mention menu (second menu state, same dropdown pattern as the slash menu).
const mentionOpen = ref(false)
const mentionItems = ref<MentionItem[]>([])
const mentionActive = ref(0)
const mentionMenuRef = ref<HTMLElement | null>(null)
let mentionDebounce: ReturnType<typeof setTimeout> | undefined
let mentionSeq = 0

const suggestionsOpen = computed(() => menuOpen.value || mentionOpen.value)
const activeListboxId = computed(() => {
  if (menuOpen.value) return SLASH_MENU_ID
  if (mentionOpen.value) return MENTION_MENU_ID
  return undefined
})
const activeOptionId = computed(() => {
  if (menuOpen.value) {
    const command = filtered.value[activeIndex.value]
    return command ? slashOptionId(command) : undefined
  }
  if (mentionOpen.value) {
    const item = mentionItems.value[mentionActive.value]
    return item ? mentionOptionId(item) : undefined
  }
  return undefined
})

function slashOptionId(command: SlashCommand) {
  return `${SLASH_MENU_ID}-${command.name}`
}

function mentionOptionId(item: MentionItem) {
  return `${MENTION_MENU_ID}-${item.ref.type}-${item.ref.id}`
}

// The menu is height-capped + scrollable, so keyboard navigation past the fold
// must scroll the highlighted option back into view (otherwise the selection
// moves to an item the user can't see).
watch(activeIndex, async () => {
  if (!menuOpen.value) return
  await nextTick()
  const el = menuRef.value?.children[activeIndex.value] as HTMLElement | undefined
  el?.scrollIntoView({ block: 'nearest' })
})

watch(mentionActive, async () => {
  if (!mentionOpen.value) return
  await nextTick()
  const el = mentionMenuRef.value?.children[mentionActive.value] as HTMLElement | undefined
  el?.scrollIntoView({ block: 'nearest' })
})

// Consume a staged draft (Ask AI buttons): copy it into the input for the user
// to review/edit, then focus so Enter sends it. `immediate` covers drafts set
// before this composer mounts (the sidebar opens in the same tick).
watch(
  () => store.composerDraft,
  async draft => {
    if (!draft) return
    inputText.value = draft
    store.composerDraft = null
    await nextTick()
    const el = textareaRef.value?.$el as HTMLTextAreaElement | undefined
    el?.focus()
  },
  { immediate: true }
)

const MAX_PENDING = 5

const modelMenuOpen = ref(false)
const selectedModelLabel = computed(() => {
  const m = store.availableModels.find(m => m.id === store.selectedModel)
  return m?.label ?? store.selectedModel ?? 'Model'
})

function selectModel(id: string) {
  store.setModel(id)
  modelMenuOpen.value = false
}

function readyAttachments() {
  return pendingAttachments.value.filter(a => !a.uploading && a.id > 0)
}

const canSend = () =>
  (inputText.value.trim() || readyAttachments().length > 0 || store.composerRefs.length > 0) &&
  !pendingAttachments.value.some(a => a.uploading || a.id < 0) &&
  !store.isStreaming &&
  !store.compactionActive &&
  store.aiEnabled !== false

async function discardPendingAttachments() {
  const toDelete = readyAttachments()
  pendingAttachments.value = []
  await Promise.all(toDelete.map(att => aiApi.deleteAttachment(att.id).catch(() => {})))
}

function clearPendingLocal() {
  pendingAttachments.value = []
}

watch(
  () => store.conversationId,
  () => {
    // Skip while a send is in flight: a new conversation's id arrives via the
    // SSE `conversation` event mid-stream, and those attachments are already
    // linked server-side. Only discard when the user actually switches/clears
    // conversations (which never happens during streaming).
    if (store.isStreaming) return
    void discardPendingAttachments()
  }
)

watch(inputText, val => {
  const q = slashMenuQuery(val)
  if (q === null) {
    menuOpen.value = false
  } else {
    filtered.value = filterCommands(q)
    menuOpen.value = filtered.value.length > 0
    activeIndex.value = 0
  }

  // The mention menu only gets the input when the slash menu didn't claim it.
  if (menuOpen.value) {
    closeMentionMenu()
    return
  }
  const mq = mentionQuery(val)
  if (mq === null) {
    closeMentionMenu()
    return
  }
  clearTimeout(mentionDebounce)
  mentionDebounce = setTimeout(() => void runMentionSearch(mq), 250)
})

function closeMentionMenu() {
  clearTimeout(mentionDebounce)
  mentionSeq++
  mentionOpen.value = false
  mentionItems.value = []
}

function handleComposerBlur() {
  menuOpen.value = false
  closeMentionMenu()
}

async function runMentionSearch(q: string) {
  const seq = ++mentionSeq
  try {
    const items = await searchMentions(q)
    if (seq !== mentionSeq) return
    mentionItems.value = items
    mentionOpen.value = items.length > 0
    mentionActive.value = 0
  } catch {
    if (seq !== mentionSeq) return
    closeMentionMenu()
  }
}

function selectMention(item: MentionItem) {
  store.addComposerRef(item.ref)
  inputText.value = stripMentionQuery(inputText.value)
  closeMentionMenu()
}

async function focusComposer() {
  await nextTick()
  const el = textareaRef.value?.$el as HTMLTextAreaElement | undefined
  el?.focus()
  el?.setSelectionRange(el.value.length, el.value.length)
}

async function selectCommand(cmd: SlashCommand) {
  menuOpen.value = false
  inputText.value = `/${cmd.name}${executesOnMenuSelect(cmd) ? '' : ' '}`
  if (executesOnMenuSelect(cmd)) {
    await handleSend()
    return
  }
  await focusComposer()
}

function sendOptions() {
  const ready = readyAttachments()
  return {
    routeContext: props.routeContext,
    currentYaml: props.currentYaml ?? store.taskEditorYaml,
    editorKind: store.editorActive ? store.yamlEditorKind : undefined,
    connectorIds: props.connectorIds ?? store.taskEditorConnectorIds,
    attachmentIds: ready.map(a => a.id),
    attachments: ready.map(({ id, filename, mime, kind, size }) => ({
      id,
      filename,
      mime,
      kind,
      size,
    })),
  }
}

async function uploadFiles(files: FileList | File[]) {
  if (store.compactionActive) {
    toast.info('Wait for conversation summarization to finish before attaching files.')
    return
  }
  const incoming = Array.from(files)
  const slots = MAX_PENDING - pendingAttachments.value.length
  if (slots <= 0) {
    toast.error(`Maximum ${MAX_PENDING} attachments per message`)
    return
  }
  const batch = incoming.slice(0, slots)
  if (batch.length < incoming.length) {
    toast.info(`Only ${batch.length} file(s) added (max ${MAX_PENDING} per message)`)
  }
  for (const file of batch) {
    const placeholder = {
      id: -Date.now(),
      filename: file.name,
      mime: file.type,
      kind: 'text' as const,
      size: file.size,
      uploading: true,
    }
    pendingAttachments.value.push(placeholder)
    try {
      const meta = await aiApi.uploadAttachment(file)
      const idx = pendingAttachments.value.indexOf(placeholder)
      if (idx >= 0) pendingAttachments.value[idx] = meta
    } catch (err) {
      pendingAttachments.value = pendingAttachments.value.filter(a => a !== placeholder)
      toast.error('Upload failed', { description: (err as Error).message })
    }
  }
}

function onFileChange(e: Event) {
  const input = e.target as HTMLInputElement
  if (input.files?.length) void uploadFiles(input.files)
  input.value = ''
}

function onPaste(e: ClipboardEvent) {
  const items = e.clipboardData?.items
  if (!items) return
  const imageFiles: File[] = []
  for (const item of items) {
    if (item.type.startsWith('image/')) {
      const file = item.getAsFile()
      if (file) imageFiles.push(file)
    }
  }
  if (imageFiles.length) {
    e.preventDefault()
    void uploadFiles(imageFiles)
  }
}

function onDrop(e: DragEvent) {
  e.preventDefault()
  dragOver.value = false
  if (e.dataTransfer?.files?.length) void uploadFiles(e.dataTransfer.files)
}

async function removeAttachment(att: AttachmentMeta) {
  pendingAttachments.value = pendingAttachments.value.filter(a => a.id !== att.id)
  if (att.id > 0) {
    try {
      await aiApi.deleteAttachment(att.id)
    } catch {
      // already removed from UI
    }
  }
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function runCommand(command: SlashCommand, args: string): Promise<boolean> {
  if (command.kind === 'action') {
    if (command.action === 'new') {
      await discardPendingAttachments()
      await store.startNewConversation()
    } else if (command.action === 'rename') {
      if (args) await store.renameConversation(args)
      else toast.info('Usage: /rename <new title>')
    } else if (command.action === 'compact') {
      await store.compactConversation()
    }
    return false
  }

  const currentYaml = props.currentYaml ?? store.taskEditorYaml
  const expanded = command.build(args, {
    hasYaml: !!currentYaml?.trim(),
    editorKind: store.editorActive ? store.yamlEditorKind : undefined,
  })
  const opts = sendOptions()
  // Clear chips before awaiting the stream: the sent user bubble renders its own
  // attachment chips, so leaving them in the composer just looks like they're
  // still queued for the next message. Ids live on in lastSendInput for retry.
  clearPendingLocal()
  await store.sendMessage(expanded, opts)
  return true
}

async function handleSend() {
  const raw = inputText.value
  if (!canSend()) return

  menuOpen.value = false
  closeMentionMenu()

  const parsed = classifySlashCommandInput(raw)
  if (parsed.kind === 'unknown') {
    toast.info(`Unknown command: /${parsed.name}`, {
      description: 'Type / to see available commands.',
    })
    await focusComposer()
    return
  }

  if (parsed.kind === 'command') {
    const validationMessage = validateSlashCommandArgs(parsed.command, parsed.args)
    if (validationMessage) {
      toast.info(validationMessage)
      if (parsed.command.argMode === 'required' && !parsed.args) {
        inputText.value = `/${parsed.command.name} `
      }
      await focusComposer()
      return
    }
    if (parsed.command.kind === 'action' && parsed.command.action === 'rename' && !store.conversationId) {
      toast.info('Send a message before renaming this conversation.')
      await focusComposer()
      return
    }
  }

  inputText.value = ''
  const opts = sendOptions()
  let didSend = true
  if (parsed.kind === 'command') {
    didSend = await runCommand(parsed.command, parsed.args)
  } else {
    clearPendingLocal()
    await store.sendMessage(raw, opts)
  }

  if (didSend) emit('send')
}

function handleKeydown(e: KeyboardEvent) {
  if (menuOpen.value && filtered.value.length) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      activeIndex.value = (activeIndex.value + 1) % filtered.value.length
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      activeIndex.value = (activeIndex.value - 1 + filtered.value.length) % filtered.value.length
      return
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      void selectCommand(filtered.value[activeIndex.value])
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      menuOpen.value = false
      return
    }
  }
  if (mentionOpen.value && mentionItems.value.length) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      mentionActive.value = (mentionActive.value + 1) % mentionItems.value.length
      return
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      mentionActive.value = (mentionActive.value - 1 + mentionItems.value.length) % mentionItems.value.length
      return
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      selectMention(mentionItems.value[mentionActive.value])
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      closeMentionMenu()
      return
    }
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void handleSend()
  }
}
</script>

<template>
  <div
    class="shrink-0 border-t border-border p-2.5"
    @dragover.prevent="dragOver = true"
    @dragleave.prevent="dragOver = false"
    @drop="onDrop"
  >
    <div class="relative">
      <div
        v-if="menuOpen && filtered.length"
        :id="SLASH_MENU_ID"
        ref="menuRef"
        class="absolute bottom-full left-0 right-0 mb-1.5 z-50 max-h-[256px] overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-md"
        role="listbox"
      >
        <button
          v-for="(cmd, i) in filtered"
          :key="cmd.name"
          :id="slashOptionId(cmd)"
          type="button"
          role="option"
          :aria-selected="i === activeIndex"
          class="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors"
          :class="i === activeIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'"
          @mousedown.prevent="selectCommand(cmd)"
          @mouseenter="activeIndex = i"
        >
          <component :is="cmd.icon" class="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <div class="min-w-0">
            <div class="text-[12px] font-medium leading-tight">
              /{{ cmd.name }}
              <span v-if="cmd.argHint" class="font-normal text-muted-foreground/50">{{ cmd.argHint }}</span>
            </div>
            <div class="text-[11px] text-muted-foreground truncate">{{ cmd.description }}</div>
          </div>
        </button>
      </div>

      <div
        v-if="mentionOpen && mentionItems.length"
        :id="MENTION_MENU_ID"
        ref="mentionMenuRef"
        class="absolute bottom-full left-0 right-0 mb-1.5 z-50 max-h-[256px] overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-md"
        role="listbox"
      >
        <button
          v-for="(item, i) in mentionItems"
          :key="`${item.ref.type}:${item.ref.id}`"
          :id="mentionOptionId(item)"
          type="button"
          role="option"
          :aria-selected="i === mentionActive"
          class="flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left transition-colors"
          :class="i === mentionActive ? 'bg-accent text-accent-foreground' : 'hover:bg-muted'"
          @mousedown.prevent="selectMention(item)"
          @mouseenter="mentionActive = i"
        >
          <AtSign class="size-3.5 mt-0.5 shrink-0 text-muted-foreground" />
          <div class="min-w-0">
            <div class="text-[12px] font-medium leading-tight truncate">{{ item.ref.label }}</div>
            <div class="text-[11px] text-muted-foreground truncate">{{ item.detail }}</div>
          </div>
        </button>
      </div>

      <div
        class="rounded-2xl border border-input bg-background shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]"
        :class="dragOver ? 'border-primary ring-2 ring-primary/30' : ''"
      >
        <input
          ref="fileInput"
          type="file"
          multiple
          class="hidden"
          accept="image/png,image/jpeg,image/gif,image/webp,.csv,.json,.yaml,.yml,.txt,.log,.md,.xml,.sql,.ts,.js,.py,.sh,.toml,.ini"
          @change="onFileChange"
        />

        <div v-if="pendingAttachments.length || store.composerRefs.length" class="flex flex-wrap gap-1.5 px-2.5 pt-2.5">
          <div
            v-for="reference in store.composerRefs"
            :key="`${reference.type}:${reference.id}`"
            class="relative group flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-1 text-[11px]"
          >
            <AtSign class="size-3.5 text-muted-foreground shrink-0" />
            <span class="max-w-[140px] truncate">{{ reference.label }}</span>
            <span class="text-muted-foreground/60">{{
              reference.type === 'account' ? reference.integration ?? 'account' : reference.type.replace('_', ' ')
            }}</span>
            <button
              type="button"
              class="ml-0.5 rounded p-0.5 text-muted-foreground hover:text-destructive"
              @click="store.removeComposerRef(reference)"
            >
              <X class="size-3" />
            </button>
          </div>
          <div
            v-for="att in pendingAttachments"
            :key="att.id"
            class="relative group flex items-center gap-1.5 rounded-md border border-border bg-muted/50 px-2 py-1 text-[11px]"
          >
            <img
              v-if="att.kind === 'image' && att.id > 0"
              :src="aiApi.attachmentUrl(att.id)"
              :alt="att.filename"
              class="size-8 rounded object-cover"
            />
            <Loader2 v-else-if="att.uploading" class="size-4 animate-spin text-muted-foreground" />
            <FileText v-else class="size-4 text-muted-foreground shrink-0" />
            <span class="max-w-[120px] truncate">{{ att.filename }}</span>
            <span v-if="att.size" class="text-muted-foreground/60">{{ formatSize(att.size) }}</span>
            <button
              type="button"
              class="ml-0.5 rounded p-0.5 text-muted-foreground hover:text-destructive"
              :disabled="att.uploading"
              @click="removeAttachment(att)"
            >
              <X class="size-3" />
            </button>
          </div>
        </div>

        <Textarea
          ref="textareaRef"
          v-model="inputText"
          :placeholder="placeholder ?? 'Ask Envoy AI…  ·  / for commands'"
          class="min-h-[40px] max-h-[160px] resize-none border-0 rounded-none bg-transparent dark:bg-transparent px-3 pt-2.5 pb-1 text-[13px] shadow-none focus-visible:ring-0"
          rows="1"
          role="combobox"
          aria-autocomplete="list"
          aria-haspopup="listbox"
          :aria-expanded="suggestionsOpen"
          :aria-controls="activeListboxId"
          :aria-activedescendant="activeOptionId"
          :disabled="store.isStreaming || store.compactionActive || store.aiEnabled === false"
          @keydown="handleKeydown"
          @paste="onPaste"
          @blur="handleComposerBlur"
        />

        <div
          class="flex items-center justify-between gap-1.5 px-1.5 pb-1.5"
          style="padding-bottom: max(0.375rem, env(safe-area-inset-bottom, 0px))"
        >
          <div class="flex items-center gap-0.5 min-w-0">
            <Button
              variant="ghost"
              size="icon-sm"
              class="size-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
              aria-label="Attach file"
              :disabled="store.isStreaming || store.compactionActive || store.aiEnabled === false"
              @click="fileInput?.click()"
            >
              <Paperclip class="size-4" />
            </Button>
            <Popover v-if="store.availableModels.length" v-model:open="modelMenuOpen">
              <PopoverTrigger as-child>
                <Button
                  variant="ghost"
                  size="sm"
                  class="h-8 min-w-0 gap-1 rounded-lg px-2 text-xs font-normal text-muted-foreground hover:text-foreground"
                  :disabled="store.isStreaming || store.compactionActive || store.aiEnabled === false"
                >
                  <span class="truncate">{{ selectedModelLabel }}</span>
                  <ChevronDown class="size-3 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" side="top" class="w-64 p-1">
                <button
                  v-for="model in store.availableModels"
                  :key="model.id"
                  type="button"
                  class="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors hover:bg-muted"
                  @click="selectModel(model.id)"
                >
                  <div class="min-w-0 flex-1">
                    <div class="text-[12px] font-medium leading-tight">
                      {{ model.label }}
                      <span v-if="model.id === store.defaultModel" class="font-normal text-muted-foreground/60"
                        >· default</span
                      >
                    </div>
                    <div class="text-[11px] text-muted-foreground truncate">{{ model.description }}</div>
                  </div>
                  <Check v-if="model.id === store.selectedModel" class="size-3.5 shrink-0 text-primary" />
                </button>
              </PopoverContent>
            </Popover>
          </div>
          <div class="flex items-center gap-1.5 shrink-0">
            <AiContextMeter />
            <Button
              v-if="store.isStreaming"
              variant="default"
              size="icon-sm"
              class="size-8 shrink-0 rounded-full"
              aria-label="Stop"
              @click="store.stopStreaming()"
            >
              <Square class="size-3.5 fill-current" />
            </Button>
            <Button
              v-else
              size="icon-sm"
              class="size-8 shrink-0 rounded-full"
              aria-label="Send"
              :disabled="!canSend()"
              @click="handleSend"
            >
              <ArrowUp class="size-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
    <p v-if="store.aiEnabled === false" class="text-[11px] text-destructive mt-1.5">
      {{ store.aiUnavailableReason || 'AI is not configured.' }}
    </p>
  </div>
</template>
